import { ReactNode } from 'react';

import { Table } from '@tanstack/react-table';

import { Collapse } from '@components/animators/collapse';
import { Button } from '@components/button';
import { ComboMenu } from '@components/combo-menu';
import { useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDropdown } from '@components/icons/dropdown';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconFilter } from '@components/icons/filter';
import { IconView } from '@components/icons/icon-view';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconPerson } from '@components/icons/person';
import { IconSettings } from '@components/icons/settings';
import { IconStar } from '@components/icons/star';
import { IconStarSolid } from '@components/icons/star-solid';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { showModal } from '@components/modal/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { CreateViewModal } from './create-view-modal';
import { DuplicateViewModal } from './duplicate-view-modal';
import { EditViewSettingsModal } from './edit-view-settings-modal';
import { FiltersScrollContainer, useViews } from './helpers';

import { ColumnControl } from '../column-control';
import { ExportCsvButton, ExportCsvMenuItem } from '../export-csv';
import { Filters, FilterConfig } from '../filters';
import { FilterState } from '../filters/common';
import { FilterActions, TableStore } from '../table-store';
import { AvailableColumn, DataItemType, TableView } from '../types';

export type ToolbarProps<DataItem extends DataItemType = DataItemType> = {
  availableColumns: AvailableColumn<DataItem>[];
  table: Table<DataItem>;
  store: TableStore;
  hasViews?: boolean;
  hideExport?: boolean;
  filterCount: number;
  filterActions: FilterActions;
  filters: FilterState;
  filterProps?: {
    config: FilterConfig;
    addFilterText: string;
    countSuffix?: string;
    showingText?: string;
  };
  columnControl?: ReactNode;
  showingCount: number;
  totalCount: number;
  countSuffix?: string;
  showingText?: string;
};

export const Toolbar = <DataItem extends DataItemType = DataItemType>({
  availableColumns,
  table,
  store,
  hasViews = false,
  hideExport = false,
  filterCount,
  filterActions,
  filters,
  filterProps,
  columnControl,
  showingCount,
  totalCount,
  countSuffix,
  showingText,
}: ToolbarProps<DataItem>) => {
  const { t } = useScopedTranslate(locale);
  const {
    views,
    selectedView,
    createView,
    selectView,
    reorderViews,
    hasUnsavedChanges,
    saveView,
    discardChanges,
  } = useViews();
  const selectedViewName = selectedView?.name ?? t('defaultView');

  return (
    <>
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack orientation="horizontal" space={4} align="center">
          {hasViews ? (
            <>
              <Menu
                placement="bottom-start"
                width={pxToRem(320)}
                trigger={Button}
                triggerProps={{
                  text: selectedViewName,
                  variant: 'secondary',
                  size: 'sm',
                  iconLeft: <IconView />,
                  iconRight: <IconDropdown />,
                  id: 'selected-view',
                  textStyle: {
                    maxWidth: pxToRem(344),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
                onDragEnd={(newOrder: Array<string>) => reorderViews(newOrder)}
              >
                <Menu.Label label={t('views')} />
                {views.map((view) => (
                  <Menu.Item
                    key={view.id}
                    id={view.id}
                    label={view.name ?? t('defaultView')}
                    sortable
                    isSortDisabled={views.length <= 1}
                    iconRight={
                      <Stack orientation="horizontal" space={1} align="center">
                        {!view.isCollaborative && (
                          <IconPerson size={16} color="buttonIconPositive" />
                        )}
                        <ViewMenu view={view} />
                      </Stack>
                    }
                    isActive={selectedView?.id === view.id}
                    onSelect={() => selectView(view.id ?? 'default')}
                  />
                ))}
                <Menu.Separator />
                <Menu.Item
                  label={t('createView')}
                  icon={<IconAddPlus />}
                  onSelect={() =>
                    showModal({
                      component: CreateViewModal as typeof CreateViewModal<DataItem>,
                      props: { createView, availableColumns },
                    })
                  }
                />
                {!hideExport && (
                  <ExportCsvMenuItem availableColumns={availableColumns} table={table} />
                )}
              </Menu>
              <Separator orientation="horizontal" />
            </>
          ) : (
            !hideExport && <ExportCsvButton availableColumns={availableColumns} table={table} />
          )}
          <Button
            text={`${t('filter')}${filterCount > 0 ? ` • ${filterCount}` : ''}`}
            variant="secondary"
            size="sm"
            iconLeft={<IconFilter color={filterCount > 0 ? 'elementBlue100' : undefined} />}
            onClick={filterActions.toggleFilters}
          />
          {columnControl ?? <ColumnControl store={store} availableColumns={availableColumns} />}
        </Stack>
        {hasViews && hasUnsavedChanges && (
          <Stack orientation="horizontal" space={2} align="center" style={{ flexShrink: 0 }}>
            <Button
              text={t('discardChanges')}
              variant="secondary"
              size="sm"
              onClick={discardChanges}
            />
            <ComboMenu
              buttonProps={{
                text: t('saveView'),
                variant: 'primary-blue',
                size: 'sm',
                onClick: () => {
                  saveView?.();
                  showSuccess(t('viewSaved'));
                },
              }}
            >
              <Menu.Item
                label={t('saveAsNewView')}
                icon={<IconAddPlus />}
                onSelect={() =>
                  showModal({
                    component: CreateViewModal as typeof CreateViewModal<DataItem>,
                    props: {
                      createView,
                      initialView: {
                        ...selectedView,
                        ...store.actions.getViewState(),
                      },
                      availableColumns,
                    },
                  })
                }
              />
            </ComboMenu>
          </Stack>
        )}
      </Stack>
      {filterProps && (
        <Collapse
          animate={{
            height: 'auto',
            transitionEnd: { overflow: 'visible' },
          }}
          isCollapsed={!filters.isOpen}
        >
          <Stack orientation="horizontal" justify="space-between" align="center">
            <FiltersScrollContainer>
              <Filters {...filterProps} state={filters} actions={filterActions} />
            </FiltersScrollContainer>
            <Text
              font="regular14"
              color="textHelper"
              text={
                showingText ??
                t('showing', {
                  showing: showingCount,
                  total: totalCount,
                  suffix: countSuffix ?? '',
                })
              }
              style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            />
          </Stack>
        </Collapse>
      )}
    </>
  );
};

const ViewMenu = ({ view }: { view: TableView }) => {
  const { t } = useScopedTranslate(locale);
  const { updateView, createView, deleteView, views, onToggleFavorite } = useViews();
  const showDeleteModal = useDeleteModal();

  const collaborativeViewCount = views.filter((view) => view.isCollaborative).length;
  const isLastCollaborative = view.isCollaborative && collaborativeViewCount <= 1;
  const isDeleteDisabled = isLastCollaborative;
  const isFavorite = Boolean(view.favoriteItem);

  return (
    <Menu
      placement="right-start"
      trigger={IconButton}
      triggerProps={{
        variant: 'ghost-purple',
        size: 'sm',
        icon: <IconMoreVertical size={16} />,
      }}
    >
      <Menu.Item
        label={t('viewSettings')}
        icon={<IconSettings />}
        isSubmenu
        onSelect={() => {
          showModal({
            component: EditViewSettingsModal,
            props: { view, updateView, isLastCollaborative },
          });
        }}
      />
      <Menu.Item
        label={t('duplicateView')}
        icon={<IconDuplicate />}
        isSubmenu
        onSelect={() => {
          showModal({ component: DuplicateViewModal, props: { view, createView } });
        }}
      />
      {onToggleFavorite && (
        <Menu.Item
          label={isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
          icon={isFavorite ? <IconStarSolid color="elementOrange100" /> : <IconStar />}
          isSubmenu
          onSelect={() => onToggleFavorite(view)}
        />
      )}
      <Menu.Item
        label={t('core.delete')}
        icon={<IconTrash />}
        variant="danger"
        isSubmenu
        isDisabled={isDeleteDisabled}
        tooltip={isDeleteDisabled ? t('cannotDeleteLastCollaborative') : undefined}
        onSelect={() => {
          if (isDeleteDisabled) {
            return;
          }
          showDeleteModal({
            itemTitle: view.name ?? t('defaultView'),
            onConfirm: () => deleteView(view.id ?? 'default'),
          });
        }}
      />
    </Menu>
  );
};

const locale = {
  en: {
    showing: 'Showing {showing} / {total} {suffix}',
    filter: 'Filter',
    hideField: 'Hide field',
    moveToStart: 'Move to start',
    moveToEnd: 'Move to end',
    selectRow: 'Select row',
    selectAllRows: 'Select all rows',
    createView: 'Create view',
    views: 'Views',
    defaultView: 'Default view',
    cannotDeleteLastView:
      'You must keep at least one view. Create an additional view to delete this one.',
    cannotDeleteLastCollaborative:
      "You can't delete the last collaborative view. Create another shared view first.",
    viewSettings: 'View settings',
    duplicateView: 'Duplicate view',
    addToFavorites: 'Add to favourites',
    removeFromFavorites: 'Remove from favourites',
    saveView: 'Save view',
    saveAsNewView: 'Save as new view',
    discardChanges: 'Discard changes',
    viewSaved: 'View updated successfully',
  },
  is: {
    showing: 'Sýni {showing} / {total} {suffix}',
    filter: 'Sía',
    hideField: 'Fela dálk',
    moveToStart: 'Færa dálk fremst',
    moveToEnd: 'Færa dálk aftast',
    selectRow: 'Velja',
    selectAllRows: 'Velja alla',
    createView: 'Búa til ásýnd',
    views: 'Ásýndir',
    defaultView: 'Sjálfgefin ásýnd',
    cannotDeleteLastView:
      'Þú verður að hafa að minnsta kosti eina ásýnd. Búðu til aðra ásýnd til að eyða þessari.',
    cannotDeleteLastCollaborative:
      'Þú getur ekki eytt síðustu sameiginlegu ásýndinni. Búðu til aðra sameiginlega ásýnd fyrst.',
    viewSettings: 'Stillingar ásýndar',
    duplicateView: 'Afrita ásýnd',
    addToFavorites: 'Bæta við eftirlæti',
    removeFromFavorites: 'Fjarlægja úr eftirlætum',
    saveView: 'Vista ásýnd',
    saveAsNewView: 'Vista sem nýja ásýnd',
    discardChanges: 'Henda breytingum',
    viewSaved: 'Ásýnd uppfærð',
  },
};
